import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, BookOpen, Mic, GitBranch, BarChart3, Users, CreditCard, CheckCircle2 } from "lucide-react";
import MarketingNav from "../components/MarketingNav";
import { BrandMark } from "../components/Logo";
import { useI18n } from "../lib/i18n";

const HERO_WAVE = "https://static.prod-images.emergentagent.com/jobs/e572816f-f9ac-4538-a2fd-6b8867912924/images/900b403a53e929294f556c53f2ccb6bc8bdcaf206bcf6c0f780f0ae5cfa98fd4.png";
const ORB = "https://static.prod-images.emergentagent.com/jobs/e572816f-f9ac-4538-a2fd-6b8867912924/images/c163fc19805eb878910bd102df172d22e2bc170c8f3805b8a7295502073065b7.png";
const MESH = "https://static.prod-images.emergentagent.com/jobs/e572816f-f9ac-4538-a2fd-6b8867912924/images/3d8543ff90d5037c393a7fcd919cb9d94b34f5e5ec668cdadfc57ad7dda64b88.png";
const ULAANBAATAR = "https://images.unsplash.com/photo-1571313991826-a1ff1f673131?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwxfHx1bGFhbmJhYXRhciUyMGNpdHlzY2FwZXxlbnwwfHx8fDE3NzgyNDE1MDJ8MA&ixlib=rb-4.1.0&q=85";

const features = [
  { key: "f_kb", icon: BookOpen },
  { key: "f_voice", icon: Mic },
  { key: "f_escalation", icon: GitBranch },
  { key: "f_analytics", icon: BarChart3 },
  { key: "f_multitenant", icon: Users },
  { key: "f_billing", icon: CreditCard },
];

const plans = [
  { id: "starter", price: "199,000", incl: ["100 minutes/сар", "Үндсэн KB", "Имэйл дэмжлэг"] },
  { id: "growth", price: "799,000", incl: ["1,000 minutes/сар", "Олон ажилтан", "Эскалацийн дүрэм", "Тэргүүлэгч дэмжлэг"], featured: true },
  { id: "enterprise", price: "Сонир.холб", incl: ["Хязгааргүй", "SLA 99.9%", "On-premise сонголт", "Хувьчилсан дуу хоолой"] },
];

export default function Landing() {
  const { t, lang } = useI18n();

  return (
    <div className="grain min-h-screen relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 -z-10">
        <img src={MESH} alt="" className="absolute inset-0 w-full h-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-b from-bg via-bg/95 to-bg" />
      </div>

      <MarketingNav />

      {/* Hero */}
      <section className="relative max-w-7xl mx-auto px-6 pt-16 lg:pt-24 pb-24 lg:pb-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 anim-stagger">
            <div className="chip" data-testid="hero-kicker">
              <Sparkles className="h-3 w-3" />
              {t("landing.kicker")}
            </div>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-extrabold tracking-tighter leading-[1.05] text-white">
              {t("landing.hero_title_a")}{" "}
              <span className="text-gradient">{t("landing.hero_title_b")}</span>
            </h1>
            <p className="mt-6 text-lg text-slate-300 max-w-2xl leading-relaxed">{t("landing.hero_sub")}</p>
            <div className="mt-8 flex flex-wrap gap-3 items-center">
              <Link to="/signup" className="btn-primary inline-flex items-center gap-2 animate-pulse-glow" data-testid="hero-cta-primary">
                {t("landing.cta_primary")} <ArrowRight className="h-4 w-4" />
              </Link>
              <a href="#demo-section" className="btn-ghost inline-flex items-center gap-2" data-testid="hero-cta-secondary">
                {t("landing.cta_secondary")}
              </a>
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="relative aspect-square">
              <img src={HERO_WAVE} alt="audio waveform" className="absolute inset-0 w-full h-full object-cover rounded-3xl shadow-glow-lg animate-orb-float" data-testid="hero-image" />
              <div className="absolute -bottom-4 -left-4 glass rounded-2xl px-4 py-3 flex items-center gap-3 shadow-card">
                <div className="wave-bars"><span/><span/><span/><span/><span/></div>
                <div className="text-xs">
                  <div className="text-white font-semibold">Live call · MN</div>
                  <div className="font-mono text-magenta">+976-9911-2233</div>
                </div>
              </div>
              <div className="absolute -top-4 -right-4 glass rounded-2xl px-4 py-3 shadow-card">
                <div className="text-[10px] uppercase tracking-widest text-slate-400">Confidence</div>
                <div className="text-2xl font-bold text-white font-mono">94<span className="text-magenta">%</span></div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats strip */}
        <div className="mt-16 grid grid-cols-3 gap-4 max-w-3xl">
          {[
            { v: "99.9%", k: "stats.uptime" },
            { v: "MN / EN", k: "stats.languages" },
            { v: "<1.2s", k: "stats.latency" },
          ].map((s) => (
            <div key={s.k} className="glass rounded-2xl p-5">
              <div className="text-3xl font-extrabold text-white tracking-tight">{s.v}</div>
              <div className="text-xs uppercase tracking-widest text-slate-400 mt-1">{t(`landing.${s.k}`)}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Features bento */}
      <section id="features" className="relative max-w-7xl mx-auto px-6 py-24">
        <div className="max-w-2xl">
          <div className="chip">02 · {t("landing.features_title")}</div>
          <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">{t("landing.features_title")}</h2>
          <p className="mt-3 text-slate-400">{t("landing.features_sub")}</p>
        </div>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4">
          {features.map((f, idx) => (
            <div key={f.key} className={`glass rounded-2xl p-7 hover:-translate-y-1 transition-all duration-300 ${idx === 0 ? "md:col-span-2" : ""}`} data-testid={`feature-${f.key}`}>
              <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-primary/30 to-magenta/30 border border-primary/30 flex items-center justify-center">
                <f.icon className="h-5 w-5 text-magenta" />
              </div>
              <h3 className="mt-5 text-xl font-bold text-white">{t(`landing.${f.key}.t`)}</h3>
              <p className="mt-2 text-slate-400 text-sm leading-relaxed">{t(`landing.${f.key}.d`)}</p>
            </div>
          ))}
          <div className="md:col-span-3 glass rounded-2xl overflow-hidden relative h-64">
            <img src={ULAANBAATAR} alt="Ulaanbaatar" className="w-full h-full object-cover opacity-40" />
            <div className="absolute inset-0 bg-gradient-to-r from-bg via-bg/70 to-transparent flex items-center px-10">
              <div>
                <div className="chip mb-3">{lang === "mn" ? "МОНГОЛЫН БИЗНЕС" : "MONGOLIAN BUSINESS"}</div>
                <h3 className="text-2xl lg:text-3xl font-bold text-white max-w-xl">
                  {lang === "mn"
                    ? "Улаанбаатарын бизнесүүдэд зориулсан, монгол хэлээр баригдсан AI"
                    : "AI built natively for Ulaanbaatar businesses"}
                </h3>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Demo callout */}
      <section id="demo-section" className="relative max-w-7xl mx-auto px-6 py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="chip">03 · DEMO</div>
            <h2 className="mt-4 text-3xl sm:text-4xl font-bold text-white tracking-tight">
              {lang === "mn" ? "Туршаад үзээрэй — " : "Try it now — "}
              <span className="text-gradient">{lang === "mn" ? "өөрөө ярилц" : "talk to it"}</span>
            </h2>
            <p className="mt-4 text-slate-400 leading-relaxed">
              {lang === "mn"
                ? "Бүртгүүлээд танай AI оператортой шууд ярилцаж, мэдлэгийн санг өөрчилж, эскалацийн дүрэм нэмэх боломжтой."
                : "Sign up, instantly chat with your AI agent, customize the knowledge base and add escalation rules."}
            </p>
            <ul className="mt-6 space-y-3 text-slate-300">
              {(lang === "mn"
                ? ["Мэдлэгийн сангаа удирдах", "Эскалацийн дүрэм нэмэх", "Дуудлагын аналитик харах", "Олон-түрээслэгчтэй платформ"]
                : ["Manage knowledge base", "Add escalation rules", "View call analytics", "Multi-tenant platform"]
              ).map((x) => (
                <li key={x} className="flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 text-magenta" />
                  <span>{x}</span>
                </li>
              ))}
            </ul>
            <Link to="/signup" className="mt-8 inline-flex btn-primary items-center gap-2" data-testid="demo-cta">
              {t("landing.cta_primary")} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="relative aspect-square max-w-md mx-auto">
            <img src={ORB} alt="AI orb" className="absolute inset-0 w-full h-full object-contain animate-orb-float" />
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="relative max-w-7xl mx-auto px-6 py-24">
        <div className="text-center max-w-2xl mx-auto">
          <div className="chip mx-auto inline-flex">04 · {t("landing.pricing_title")}</div>
          <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight">{t("landing.pricing_title")}</h2>
          <p className="mt-3 text-slate-400">{t("landing.pricing_sub")}</p>
        </div>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
          {plans.map((p) => (
            <div key={p.id} className={`relative rounded-2xl p-8 ${p.featured ? "glass glow-border" : "glass"} ${p.featured ? "scale-[1.02] shadow-glow" : ""}`} data-testid={`pricing-${p.id}`}>
              {p.featured && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-gradient-to-r from-primary to-magenta text-xs font-bold text-white">{t("landing.plan_popular")}</div>
              )}
              <div className="text-sm font-bold uppercase tracking-widest text-magenta">{t(`landing.plan_${p.id}`)}</div>
              <div className="mt-4 text-4xl font-extrabold text-white tracking-tight">
                {p.price === "Сонир.холб" ? p.price : `₮${p.price}`}
                {p.price !== "Сонир.холб" && <span className="text-base font-medium text-slate-400">/{lang === "mn" ? "сар" : "mo"}</span>}
              </div>
              <ul className="mt-6 space-y-3 text-sm text-slate-300">
                {p.incl.map((x) => (
                  <li key={x} className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-magenta" /> {x}</li>
                ))}
              </ul>
              <Link to="/signup" className={`mt-8 inline-flex w-full justify-center ${p.featured ? "btn-primary" : "btn-ghost"}`} data-testid={`pricing-cta-${p.id}`}>
                {t("landing.plan_cta")}
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 py-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-slate-500">
          <BrandMark size="sm" />
          <div>{t("landing.footer")}</div>
        </div>
      </footer>
    </div>
  );
}
